import SymbolLayer from './SymbolLayer';
import Feature from '../render/Feature';

/**
 * 矢量数据绘制图层
 */
export default class PartitionLayer extends SymbolLayer {
    /**
     * 构造函数
     * @param {string} id 图层ID
     * @param {Object} options 可选项
     */
    constructor(id, options) {
        super(id, options);

        /**
         * 渲染器
         * 参数为Feature对象
         * @type {object<render.Render>}
         */
        this.render = this.options.render;
        /**
         * 矢量数据生成器函数
         * 参数为map和layer对象
         * 返回值为数组{array<object[geometry, properties, symbol]>}
         * @type {function}
         */
        this.generator = this.options.generator;
        /**
         * 图层中的全部要素对象
         * @type {array<Feature>}
         */
        this.data = {};
        
        this._added = {}; // 添加到图层中的要素对象数组
        this._addedSymbols = {}; // 添加到图层中的要素的渲染符号数组
    }

    /**
     * 向图层中添加要绘制的对象
     * @param {array<object[geometry, properties, symbol]>} items 要绘制的数据列表，数据中需要有geometry|properties|symbol属性信息
     */
    add(items) {
        for (const [key, val] of Object.entries(items)) {
            const data = this._fromData(val);
            if (data) {
                if(this._added.hasOwnProperty(key)) {
                    this._added[key].push(...data.features);
                } else {
                    this._added[key] = data.features;
                }
                if(this._addedSymbols.hasOwnProperty(key)) {
                    this._addedSymbols[key].push(...data.symbols);
                } else {
                    this._addedSymbols[key] = data.symbols;
                }

                if (data.symbols.length > 0) {
                    this._resourceLoaded = false;
                    this.redraw();
                }
            }
        }
    }

    removePartition(items) {
        items.forEach(item => {
            if(this._added.hasOwnProperty(item)) {
                delete this._added[item]
            }
            if(this.data.hasOwnProperty(item)) {
                delete this.data[item]
            }
            if(this._addedSymbols.hasOwnProperty(item)) {
                delete this._addedSymbols[item]
            }
        })
        this._resourceLoaded = false;
        if (items.length > 0) {
            this.redraw();
        }
    }

    /**
     * 清空所有绘制的对象
     */
    clear() {
        this.data = {};

        this._added = {};
        this._addedSymbols = {};

        this._clear();
    }

    /**
     * 获取要绘制的符号列表
     * @returns {array} 符号数组
     */
    getSymbols() {
        const symbols = [];
        let zoom = this._map.getZoom();
        this.data = {...this._added}
        for (let key in this._addedSymbols) {
            for (let index = 0; index < this._addedSymbols[key].length; index++) {
                const element = this._addedSymbols[key][index];
                if (element.hasOwnProperty('maxZoom') && zoom > element.maxZoom) {
                    continue;
                }
                if (element.hasOwnProperty('minZoom') && zoom < element.minZoom) {
                    continue;
                }
                symbols.push(element);
            }
        }
        
        if (this.generator) {
            const generated = this._runGenerator();
            if (generated) {
                this.data.push(...generated.features);
                symbols.push(...generated.symbols);
                if (generated.symbols.length > 0) {
                    this._resourceLoaded = false;
                }
            }
        }

        return symbols;
    }

    /**
     * 执行生成器函数，并生成要素和渲染符号
     */
    _runGenerator() {
        const items = this.generator(this._map, this);
        
        return this._fromData(items);
    }

    /**
     * 根据数据列表，生成要素和符号列表
     * @param {array<object[geometry, properties, symbol]>} items 要绘制的数据列表，数据中需要有geometry|properties|symbol属性信息
     */
    _fromData(items) {
        if (!items || !items.length) {
            return null;
        }

        const features = [];
        const symbols = [];
        for (const item of items) {
            if (item.geometry) {
                const feature = new Feature(item.geometry, item.properties);
                const ftSymbols = this._symbolizeFeature(feature, item.symbol, item);
                features.push(feature);
                symbols.push(...ftSymbols);
            }
        }

        return {
            features,
            symbols
        };
    }

    /**
     * 根据要素和符号信息生成符号对象
     * @param {object<Feature>} feature    要素对象
     * @param {object<geojson>} symbolJson 符号信息
     * @returns {array<Symbol>} 符号对象数组
     */
    _symbolizeFeature(feature, symbolJson, featureData) {
        if (!feature) {
            return [];
        }

        const symbols = [];
        const geometry = this.createGeometry(feature.geometry);
        let symbol;
        if (symbolJson) {
            symbol = this.createSymbol(symbolJson);
            symbol.geometry = geometry;
            if (featureData.maxZoom) {
                symbol.maxZoom = featureData.maxZoom;
            }
            if (featureData.minZoom) {
                symbol.minZoom = featureData.minZoom;
            }
            symbols.push(symbol);
        } else if (this.render) {
            symbol = this.render.getSymbol(feature);
            if (featureData.maxZoom) {
                symbol.maxZoom = featureData.maxZoom;
            }
            if (featureData.minZoom) {
                symbol.minZoom = featureData.minZoom;
            }
            if (symbol) {
                if (Array.isArray(symbol)) {
                    symbols.push(...symbol);
                } else {
                    symbols.push(symbol);
                }
            }
        } else if (this._defaultSymbol) {
            symbol = this.createSymbol(this._defaultSymbol);
            symbol.geometry = geometry;
            if (featureData.maxZoom) {
                symbol.maxZoom = featureData.maxZoom;
            }
            if (featureData.minZoom) {
                symbol.minZoom = featureData.minZoom;
            }
            symbols.push(symbol);
        }

        symbols.forEach(sl => {
            sl.setResourceFactory(this._map.ImageLoader);
        });

        return symbols;
    }
}
